import { useState } from "react";
import { motion, useScroll, useMotionValueEvent, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";

const LINKS = [
  { label: "Audio", target: "#audio" },
  { label: "Custom", target: "#custom" },
  { label: "About", target: "#about" },
  { label: "Process", target: "#process" },
];

export const LogoMark = ({ size = 36 }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
    <rect width="64" height="64" rx="14" fill="#12141B" stroke="rgba(255,255,255,0.12)" />
    <g fill="#FF5500">
      <rect x="11" y="25" width="6" height="14" rx="3" />
      <rect x="20" y="17" width="6" height="30" rx="3" />
      <rect x="29" y="29" width="6" height="6" rx="3" />
      <rect x="38" y="11" width="6" height="42" rx="3" />
      <rect x="47" y="21" width="6" height="22" rx="3" />
    </g>
  </svg>
);

export default function Nav({ scrollTo }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { scrollY } = useScroll();
  useMotionValueEvent(scrollY, "change", (v) => setScrolled(v > 40));

  const go = (target) => {
    setOpen(false);
    scrollTo(target);
  };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-[#0A0B0E]/85 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => go(0)}
          className="flex items-center gap-3 group"
        >
          <LogoMark />
          <span className="font-display font-extrabold tracking-tight text-sm sm:text-base leading-none text-left">
            MOBILE ONE
            <span className="block font-tech text-[10px] tracking-[0.4em] text-[#FF5500] font-medium mt-1">
              AUDIO
            </span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <button
              key={l.target}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              onClick={() => go(l.target)}
              className="font-tech text-xs uppercase tracking-[0.25em] text-white/60 hover:text-[#FF5500] transition-colors duration-300"
            >
              {l.label}
            </button>
          ))}
          <a
            data-testid="nav-call-button"
            href="tel:3022686466"
            className="flex items-center gap-2 rounded-full bg-[#FF5500] px-5 py-2.5 font-tech text-xs font-bold uppercase tracking-widest text-white transition-transform duration-300 hover:scale-105 volt-glow"
          >
            <Phone size={14} />
            (302) 268-6466
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          className="md:hidden text-white"
          onClick={() => setOpen(!open)}
          aria-label="Toggle menu"
        >
          {open ? <X size={26} /> : <Menu size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="md:hidden overflow-hidden bg-[#0A0B0E]/95 backdrop-blur-xl border-b border-white/10"
          >
            <div className="px-6 py-6 flex flex-col gap-5">
              {LINKS.map((l) => (
                <button
                  key={l.target}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
                  onClick={() => go(l.target)}
                  className="text-left font-display text-2xl font-bold text-white/80 hover:text-[#FF5500] transition-colors"
                >
                  {l.label}
                </button>
              ))}
              <a
                data-testid="nav-mobile-call-button"
                href="tel:3022686466"
                className="flex items-center justify-center gap-2 rounded-full bg-[#FF5500] px-5 py-3.5 font-tech text-xs font-bold uppercase tracking-widest"
              >
                <Phone size={14} /> (302) 268-6466
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
